import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {form, required, validate, disabled} from '@angular/forms/signals';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import {Availability} from '../../../../classes/models/employee/Availability';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {EditType} from '../../../../classes/enums/EditType';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {AvailabilityValidator} from '../../../../validators/availability-validator.service';
import {DayOfWeek} from '../../../../classes/enums/DayOfWeek';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeAvailabilityService} from '../../../../services/http/employee/employee-availability.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Signal form model for an availability. */
export interface AvailabilityFormModel {
  startTime: string;
  endTime: string;
  useOpeningTime: boolean;
  useClosingTime: boolean;
}

/** Data interface for opening the availability edit modal. */
export interface AvailabilityEditCalendarDayData {
  employeePositionId: EmployeePositionId;
  startDate: string;
  dayOfWeek: DayOfWeek;
  availabilities: Map<DayOfWeek, Availability>;
  storeWorkingTime: StoreWorkingTime;
  weekExists: boolean;
  editType: EditType;
}

/**
 * This component creates a signal form in a modal for creating, updating or deleting an availability.
 */
@Component({
  selector: 'app-availability-edit-calendar-day',
  templateUrl: './availability-edit-calendar-day.component.html',
  styleUrls: ['./availability-edit-calendar-day.component.less'],
  imports: [SHARED_IMPORTS]
})
export class AvailabilityEditCalendarDayComponent implements OnInit {

  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** The date, starting from which the availabilities will take effect. */
  readonly startDate = signal<string>(undefined);
  /** The day of week, which is being edited by the user. */
  readonly dayOfWeek = signal<DayOfWeek>(undefined);
  /** Availabilities for the whole week to be created or updated. */
  readonly availabilities = signal<Map<DayOfWeek, Availability>>(undefined);
  /** Working time of the selected store. */
  readonly storeWorkingTime = signal<StoreWorkingTime>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal<boolean>(undefined);
  /** An edit action, which is performed by a user. */
  readonly editType = signal<EditType>(undefined);

  private readonly employeeAvailabilityService = inject(EmployeeAvailabilityService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly destroyRef = inject(DestroyRef);

  /** Assigning an EditType, so it could be used in HTML. */
  EditType = EditType;

  /** A success message which is computed based on the current edit type. */
  readonly successMessage = computed(() => {
    const defaultMessage = 'Availability has been successfully ';
    return {
      [EditType.CREATE]: defaultMessage + 'created',
      [EditType.EDIT]: defaultMessage + 'edited',
      [EditType.DELETE]: defaultMessage + 'deleted'
    }[this.editType()];
  });

  /** Signal tracking whether a request to backend is in process. */
  readonly loading = signal(false);

  /** Signal form model for an availability. */
  readonly formModel = signal<AvailabilityFormModel>({
    startTime: '',
    endTime: '',
    useOpeningTime: false,
    useClosingTime: false
  });

  /** Signal form with validation schema for an availability. */
  readonly availabilityForm = form(this.formModel, (path) => {
    required(path.startTime);
    required(path.endTime);

    validate(path.startTime, ({value}) => {
      if (!new RegExp(AvailabilityValidator.TIME_REGEX).test(value() as string)) {
        return {kind: 'pattern', message: 'Start time must be in 15 minutes increments'};
      }
      return undefined;
    });

    // Pattern validation for end time in 15-minute increments
    validate(path.endTime, ({value}) => {
      if (!new RegExp(AvailabilityValidator.TIME_REGEX).test(value() as string)) {
        return {kind: 'pattern', message: 'End time must be in 15 minutes increments'};
      }
      return undefined;
    });
    // Start time is disabled when "use shop opening time" checkbox is checked
    disabled(path.startTime, ({valueOf}) => valueOf(path.useOpeningTime) as boolean);
    // End time is disabled when "use shop closing time" checkbox is checked
    disabled(path.endTime, ({valueOf}) => valueOf(path.useClosingTime) as boolean);

    // Cross-field validation: start time must be earlier than end time and shift must meet minimum duration
    validate(path.endTime, ({value, valueOf}) => {
      const startTime = moment(valueOf(path.startTime) as string, 'HH:mm:ss');
      const endTime = moment(value() as string, 'HH:mm:ss');

      if (startTime.isSameOrAfter(endTime)) {
        return {kind: 'startTimeIsAfterEndTime', message: 'Start time must be earlier than end time'};
      }

      const duration = moment.duration(endTime.diff(startTime));
      if (duration.asHours() < AvailabilityValidator.MIN_SHIFT_LENGTH_IN_HOURS) {
        return {kind: 'tooShortShift', message: 'The duration is too short'};
      }

      return undefined;
    });
  });

  static open(modalService: NgbModal, data: AvailabilityEditCalendarDayData): NgbModalRef {
    const modalRef = modalService.open(AvailabilityEditCalendarDayComponent);
    const instance = modalRef.componentInstance as AvailabilityEditCalendarDayComponent;
    instance.employeePositionId.set(data.employeePositionId);
    instance.startDate.set(data.startDate);
    instance.dayOfWeek.set(data.dayOfWeek);
    instance.availabilities.set(data.availabilities);
    instance.storeWorkingTime.set(data.storeWorkingTime);
    instance.weekExists.set(data.weekExists);
    instance.editType.set(data.editType);
    return modalRef;
  }

  /**
   * Initializes the form model from component signals.
   */
  ngOnInit(): void {
    const availability = this.availabilities().get(this.dayOfWeek());
    const swt = this.storeWorkingTime();
    const startTime = availability?.startTime ?? swt.openingTime;
    const endTime = availability?.endTime ?? swt.closingTime;

    this.formModel.set({
      startTime,
      endTime,
      useOpeningTime: startTime === swt.openingTime,
      useClosingTime: endTime === swt.closingTime
    });
  }

  /**
   * Sets start time to store opening time when the "use shop opening time" checkbox is checked.
   */
  toggleUseShopOpeningTime(): void {
    if (this.formModel().useOpeningTime) {
      this.formModel.update(m => ({...m, startTime: this.storeWorkingTime().openingTime}));
    }
  }

  /**
   * Sets end time to store closing time when the "use shop closing time" checkbox is checked.
   */
  toggleUseShopClosingTime(): void {
    if (this.formModel().useClosingTime) {
      this.formModel.update(m => ({...m, endTime: this.storeWorkingTime().closingTime}));
    }
  }

  /**
   * Sends an availability create or update request to backend. HTTP POST request is sent if a week does not exist in backend,
   * otherwise PUT request is sent.
   * @param modalComponent a modal component.
   */
  onSubmit(modalComponent: ModalComponent): void {
    this.loading.set(true);

    const availabilities: Map<DayOfWeek, Availability> = new Map(this.availabilities());
    availabilities.delete(this.dayOfWeek());

    if (this.editType() !== EditType.DELETE) {
      const formValue = this.formModel();
      const availability = new Availability(
              this.dayOfWeek(),
              formValue.startTime,
              formValue.endTime,
              this.startDate()
      );
      availabilities.set(this.dayOfWeek(), availability);
    }

    if (this.weekExists()) {
      this.editAvailability(modalComponent, availabilities);
    } else {
      this.addAvailabilities(modalComponent, availabilities);
    }
  }

  private editAvailability(modalComponent: ModalComponent, availabilities: Map<DayOfWeek, Availability>) {
    this.employeeAvailabilityService.editAvailability(this.employeePositionId(), this.startDate(), availabilities)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success(this.successMessage());
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private addAvailabilities(modalComponent: ModalComponent, availabilities: Map<DayOfWeek, Availability>) {
    this.employeeAvailabilityService.addAvailabilities(this.employeePositionId(), this.startDate(), availabilities)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success(this.successMessage());
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}